<template>
  <div class="min-h-screen bg-gray-100">
    <!-- Header -->
    <header class="bg-black text-white shadow-lg">
      <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div class="flex justify-between items-center py-4">
          <div>
            <h1 class="text-2xl font-maax font-bold">83 Islands Admin</h1>
            <p class="text-sm text-gray-400">Management Dashboard</p>
          </div>
          <div class="flex items-center space-x-4">
            <router-link
              to="/"
              class="text-gold hover:text-white transition-colors text-sm"
            >
              View Website
            </router-link>
            <button
              @click="handleLogout"
              class="bg-red-600 hover:bg-red-700 text-white px-4 py-2 rounded-lg text-sm font-medium transition-all"
            >
              Logout
            </button>
          </div>
        </div>
      </div>
    </header>

    <!-- Main Content -->
    <main class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <!-- Tabs Navigation -->
      <div class="bg-white rounded-lg shadow mb-6">
        <nav class="flex border-b border-gray-200">
          <button
            v-for="tab in tabs"
            :key="tab.id"
            @click="activeTab = tab.id"
            :class="[
              'px-6 py-4 text-sm font-medium transition-all',
              activeTab === tab.id
                ? 'border-b-2 border-gold text-black'
                : 'text-gray-500 hover:text-gray-700'
            ]"
          >
            {{ tab.label }}
          </button>
        </nav>
      </div>

      <!-- Tab Content -->
      <div class="bg-white rounded-lg shadow-lg p-6">
        <!-- Bookings Calendar Tab -->
        <div v-if="activeTab === 'calendar'" class="space-y-6">
          <BookingsCalendar />
        </div>

        <!-- Bookings List Tab -->
        <div v-if="activeTab === 'bookings'" class="space-y-6">
          <BookingsList />
        </div>

        <!-- Holiday Management Tab -->
        <div v-if="activeTab === 'holidays'" class="space-y-6">
          <HolidayManager />
        </div>

        <!-- Google Business Profile Tab -->
        <div v-if="activeTab === 'google'" class="space-y-6">
          <GoogleBusinessManager />
        </div>

        <!-- Email Campaigns Tab -->
        <div v-if="activeTab === 'email'" class="space-y-6">
          <EmailCampaignManager />
        </div>

        <!-- Jobs & Recruitment Tab -->
        <div v-if="activeTab === 'jobs'" class="space-y-6">
          <JobsManager />
        </div>

        <!-- Gift Vouchers Tab -->
        <div v-if="activeTab === 'vouchers'" class="space-y-6">
          <GiftVoucherManager />
        </div>
      </div>
    </main>
  </div>
</template>

<script setup>
import { ref, onMounted } from 'vue'
import { useRouter } from 'vue-router'
import { useAuth } from '../../composables/useAuth'
import BookingsCalendar from '../../components/admin/BookingsCalendar.vue'
import BookingsList from '../../components/admin/BookingsList.vue'
import HolidayManager from '../../components/admin/HolidayManager.vue'
import GoogleBusinessManager from '../../components/admin/GoogleBusinessManager.vue'
import EmailCampaignManager from '../../components/admin/EmailCampaignManager.vue'
import JobsManager from '../../components/admin/JobsManager.vue'
import GiftVoucherManager from '../../components/admin/GiftVoucherManager.vue'

const router = useRouter()
const { logout, verifyToken, isAuthenticated } = useAuth()
const activeTab = ref('calendar')

const tabs = [
  { id: 'calendar', label: 'Calendar View' },
  { id: 'bookings', label: 'Bookings List' },
  { id: 'holidays', label: 'Manage Holidays' },
  { id: 'google', label: 'Google Business' },
  { id: 'email', label: 'Email Campaigns' },
  { id: 'jobs', label: 'Recruitment' },
  { id: 'vouchers', label: 'Gift Vouchers' }
]

const handleLogout = () => {
  logout()
  router.push('/admin/login')
}

// Verify authentication on mount
onMounted(async () => {
  if (!isAuthenticated.value) {
    router.push('/admin/login')
    return
  }

  const valid = await verifyToken()
  if (!valid) {
    router.push('/admin/login')
  }
})
</script>

<style scoped>
.text-gold {
  color: #F6D993;
}

.border-gold {
  border-color: #F6D993;
}
</style>
